import type { FindingOut } from '@/api/types';

/** User-controlled column sort for the List view. Pure + exported so the
 *  comparator + the header-click cycle are unit-tested without the table.
 *  When no sort is active the view falls back to the shared locked order. */
export type SortKey = 'amount' | 'date' | 'status' | 'supplier';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: SortKey;
  dir: SortDir;
}

// Work-queue order: un-dispositioned first, then decided.
const STATUS_RANK: Record<string, number> = {
  pending: 0,
  needs_review: 1,
  approved: 2,
  rejected: 3,
};

/** Cycle a column through desc → asc → off as its header is clicked.
 *  Clicking a different column starts it at desc. */
export function nextSortState(current: SortState | null, key: SortKey): SortState | null {
  if (!current || current.key !== key) return { key, dir: 'desc' };
  if (current.dir === 'desc') return { key, dir: 'asc' };
  return null;
}

function billDate(f: FindingOut): string {
  const v = f.evidence['bill_date'];
  return typeof v === 'string' ? v : '';
}

function supplierName(f: FindingOut): string {
  const v = f.evidence['supplier.name'];
  return typeof v === 'string' ? v : '';
}

/** Return a sorted COPY of `rows` for the active sort, or `rows` unchanged
 *  when no sort is active (preserving the caller's locked order + its array
 *  reference). Ties break by finding_id so the order is deterministic. */
export function sortFindings(rows: FindingOut[], sort: SortState | null): FindingOut[] {
  if (!sort) return rows;
  const mul = sort.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    let d = 0;
    if (sort.key === 'amount') {
      d = (Number(a.recovery.amount) || 0) - (Number(b.recovery.amount) || 0);
    } else if (sort.key === 'date') {
      d = billDate(a).localeCompare(billDate(b));
    } else if (sort.key === 'supplier') {
      d = supplierName(a).localeCompare(supplierName(b));
    } else {
      d = (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
    }
    if (d !== 0) return d * mul;
    return a.finding_id.localeCompare(b.finding_id);
  });
}
